<template>
  <component :is="card ? PCard : 'div'" class="content-cta-form">
    <SHeading v-if="title" :color="color" level="2" size="3">
      {{ title }}
    </SHeading>
    <form
      class="content-cta-form__form"
      :class="{ 'content-cta-form__form--bordered': card }"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <STextInput
        v-model="zipCode"
        :aria-label="
          hideLabel
            ? tr('main_site.company_wall.hero_form.zip_code_label')
            : undefined
        "
        autocomplete="postal-code"
        class="content-cta-form__input"
        :label="
          hideLabel
            ? undefined
            : tr('main_site.company_wall.hero_form.zip_code_label')
        "
        name="zip_code"
        :placeholder="
          tr('main_site.company_wall.hero_form.zip_code_placeholder')
        "
      />
      <SButton
        class="content-cta-form__button"
        color="accent"
        :loading="isLoading"
        suffix-icon="far fa-arrow-right"
        type="submit"
      >
        {{
          submitLabel ||
          (goToLocalSearch
            ? tr("main_site.company_wall.hero_form.submit_button_local_search")
            : tr("main_site.company_wall.hero_form.submit_button"))
        }}
      </SButton>
    </form>
    <div
      v-if="$slots.bottom"
      class="content-cta-form__bottom"
      :class="{ 'content-cta-form__bottom--bordered': card }"
    >
      <slot name="bottom" />
    </div>
    <HeaderBenefits
      v-if="showUsps"
      class="content-cta-form__trust"
      :class="[{ 'content-cta-form__trust--bordered': card }]"
      :color="color"
    />
  </component>
</template>

<script setup lang="ts">
import { useMutation } from "@pinia/colada";
import { navigateTo } from "#app";
import { ref } from "vue";

import { SButton } from "@solvari/common-fe";
import { zipcodeRule } from "@solvari/common-fe/validation";
import { PCard, SHeading, STextInput } from "@solvari/components";
import { useI18nMainSiteCompanyWall } from "@solvari/translations";

import { ziggy } from "@/shared/libs/ziggy";
import { useResolveZipcode } from "~/shared/api/geocoder.api";
import HeaderBenefits from "~/shared/components/header/HeaderBenefits.vue";
import { useHostLocale } from "~/shared/composables/useHost";

const props = defineProps<{
  card?: boolean;
  color?: "gray" | "white";
  formSubmitUrl: string;
  geoProductSlug?: string;
  goToLocalSearch?: boolean;
  hideLabel?: boolean;
  showUsps?: boolean;
  submitLabel?: string | null;
  title?: string;
}>();

const { tr } = useI18nMainSiteCompanyWall();
const zipCode = ref("");
const locale = useHostLocale();

const { mutateAsync: resolveZipcode, isLoading } =
  useMutation(useResolveZipcode());

async function handleSubmit() {
  // Unresolvable zipcode → endpoint 422s; go straight to the offerte redirect.
  if (
    props.goToLocalSearch &&
    props.geoProductSlug &&
    zipcodeRule(locale).validate(zipCode.value)
  ) {
    try {
      const result = await resolveZipcode({
        localeCode: locale,
        zipcode: zipCode.value,
      });

      void navigateTo(
        ziggy("mainSite.localSearch.municipality", {
          geoProductSlug: props.geoProductSlug,
          municipality: result.municipalitySlug,
          province: result.provinceSlug,
        }),
      );
      return;
    } catch {
      // 404 (no match) or network error → fall through to offerte redirect
    }
  }

  void navigateTo(
    `${props.formSubmitUrl}?zip_code=${encodeURIComponent(zipCode.value)}`,
  );
}
</script>

<style lang="postcss">
.content-cta-form {
  &__form {
    @apply mb-4 flex w-auto flex-col gap-2;

    &--bordered {
      @apply border-b pb-4;
    }

    @screen 600 {
      @apply flex-row;
    }
  }

  &__input {
    @screen 600 {
      @apply mt-auto max-w-full flex-1;
    }

    label {
      @apply sr-only;

      @screen 600 {
        @apply not-sr-only block;
      }
    }
  }

  &__button {
    @apply ml-auto w-full;

    @screen 600 {
      @apply mt-auto w-auto;
    }
  }

  &__bottom {
    @apply mb-4;

    &--bordered {
      @apply border-b pb-4;
    }
  }

  &__trust {
    @apply text-sm;
  }
}
</style>
